/**
 * i18n – single entry point for translations.
 *
 * How it works:
 * - English (en) is the source of truth in locales/en.js.
 * - Each other locale can be partial; missing keys fall back to English.
 * - To add a language: add locales/xx.js (at least { 'lang.xx': 'Language name' }) and register below.
 */

import en from './locales/en.js'

/** Other languages load on demand so the English marketing pages do not download every dictionary. */
const LOCALE_LOADERS = {
  es: () => import('./locales/es.js'),
  pt: () => import('./locales/pt.js'),
  ja: () => import('./locales/ja.js'),
  zh: () => import('./locales/zh.js'),
  hi: () => import('./locales/hi.js'),
  fr: () => import('./locales/fr.js'),
  it: () => import('./locales/it.js'),
  de: () => import('./locales/de.js'),
  ar: () => import('./locales/ar.js'),
}

/** Merge a locale with en so missing keys fall back to English. */
function mergeWithEn(localeObj) {
  if (!localeObj || typeof localeObj !== 'object') return { ...en }
  return { ...en, ...localeObj }
}

/** English is ready immediately. Other codes are filled in by ensureLocale. */
export const translations = { en: mergeWithEn(en) }

export function hasLocale(code) {
  return code === 'en' || Object.prototype.hasOwnProperty.call(LOCALE_LOADERS, code)
}

const pending = new Map()

/** Load one non-English dictionary before the UI switches to it. */
export function ensureLocale(code) {
  if (!code || code === 'en') return Promise.resolve(translations.en)
  if (translations[code]) return Promise.resolve(translations[code])
  const loader = LOCALE_LOADERS[code]
  if (!loader) return Promise.resolve(translations.en)
  if (!pending.has(code)) {
    pending.set(
      code,
      loader()
        .then((mod) => {
          translations[code] = mergeWithEn(mod.default)
          return translations[code]
        })
        .catch((err) => {
          pending.delete(code)
          throw err
        }),
    )
  }
  return pending.get(code)
}

/** Language selector: English, Spanish, Portuguese, Japanese, Chinese, Hindi, French, Italian, German, Arabic. */
export const supportedLanguages = [
  { code: 'en', labelKey: 'lang.en', flag: '🇺🇸' },
  { code: 'es', labelKey: 'lang.es', flag: '🇪🇸' },
  { code: 'pt', labelKey: 'lang.pt', flag: '🇵🇹' },
  { code: 'ja', labelKey: 'lang.ja', flag: '🇯🇵' },
  { code: 'zh', labelKey: 'lang.zh', flag: '🇨🇳' },
  { code: 'hi', labelKey: 'lang.hi', flag: '🇮🇳' },
  { code: 'fr', labelKey: 'lang.fr', flag: '🇫🇷' },
  { code: 'it', labelKey: 'lang.it', flag: '🇮🇹' },
  { code: 'de', labelKey: 'lang.de', flag: '🇩🇪' },
  { code: 'ar', labelKey: 'lang.ar', flag: '🇦🇪' },
]

export default translations
